import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCopy } from "react-icons/fi";
import { MdCheck, MdContentCopy, MdImage } from "react-icons/md";

import { ReactComponent as ImageLibraryIcon } from "../../../assets/imageLibrary.svg";
import courseServices from "../../../services/courseServices";
import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import Modal from "../../Modal";
import Pagination from "../../Pagination";

const ImageLibrary = forwardRef(({}, ref) => {
  const lang = getLanguage();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [copySuccess, setCopySuccess] = useState(null);

  useImperativeHandle(ref, () => ({
    openModal() {
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const res = await courseServices.getSmoothyImageLibrary();
      setCategories(res);
      if (res.length > 0) {
        fetchImages(res[0].id);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchImages = async (categoryId, page = 1) => {
    try {
      const res = await courseServices.getSmoothyImageByID(categoryId, {
        per_page: 9,
        sort_by: "name",
        sort_dir: "asc",
        page,
      });

      setImages(res.data);
      setSelectedCategory(categoryId);
      setLastPage(res?.last_page);
      setTotalPages(res?.total);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      fetchImages(selectedCategory, currentPage);
    }
  }, [currentPage, lang]);

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleCopyPath = (path) => {
    const textarea = document.createElement("textarea");
    textarea.value = path;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      setCopySuccess(path);
      setTimeout(() => setCopySuccess(null), 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <p className="flex items-center gap-2 text-blue-500">
          <ImageLibraryIcon size={22} />
          {t("image_library")}
        </p>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("cancel")} />
        </div>
      }
    >
      <div className="flex max-h-[30rem]">
        {/* Categories menu */}
        <div className="custom-scrollbar r w-1/3 overflow-y-auto border-r px-1 py-4 dark:border-dark-border">
          <h3 className="mb-3 text-lg font-semibold">{t("categories")}</h3>
          <ul className="space-y-2">
            {categories.map((category) => (
              <li
                key={category.id}
                className={`cursor-pointer rounded px-3 py-2 text-sm transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-900 ${
                  selectedCategory === category.id
                    ? "bg-blue-100 font-medium text-blue-500 dark:bg-gray-900"
                    : ""
                }`}
                onClick={() => fetchImages(category.id)}
              >
                {category.name}
              </li>
            ))}
          </ul>
        </div>

        {/* Images list */}
        <div className="custom-scrollbar w-2/3 overflow-y-auto p-4">
          <h3 className="mb-3 text-lg font-semibold">{t("images")}</h3>
          <div className="grid grid-cols-2 gap-4">
            {images.length > 0 ? (
              images.map((image) => {
                const fullPath = `${process.env.REACT_APP_API_S3_URL}${image.url}`;
                return (
                  <div
                    key={image.id}
                    className="group relative overflow-hidden rounded-lg border bg-white p-2 shadow-sm transition hover:shadow-md dark:border-dark-border dark:bg-gray-900"
                  >
                    <img src={fullPath} alt={image.name} className="h-32 w-full rounded object-contain" />
                    <p className="truncate font-semibold text-blue-500">{image.name}</p>
                    <Button
                      label={t("copy_path")}
                      className="!px-0"
                      color="white"
                      variant="text"
                      iconLeft={
                        copySuccess === image.url ? (
                          <MdCheck size={20} className="text-green-500" />
                        ) : (
                          <MdContentCopy size={20} />
                        )
                      }
                      onClick={() => handleCopyPath(image.url)}
                    />
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500">{t("no_images_available")}</p>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 9 && (
            <div className="mt-6">
              <Pagination lastPage={lastPage} currentPage={currentPage} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default ImageLibrary;
